/**
 * The primitives every screen uses.
 *
 * Built against `design-system/tokens.css` — **never a hex, a px spacing value,
 * a radius or a shadow written into a component** (`docs/DESIGN-SYSTEM.md` §3).
 * No shadcn: the client owns the IP and would inherit its conventions for years.
 */
import type { ReactNode } from 'react'
import { InfoDot } from './InfoDot'

export function Card({
  title,
  info,
  action,
  children,
  footer,
}: {
  title?: string
  /**
   * A caveat or explanation, as an `InfoDot` beside the title.
   *
   * **This is where an explanation goes now.** `footer` was carrying both
   * provenance (*"table X, 9 columns, filtered on Y"*) and explanation
   * (*"Median, not mean, so one viral spotlight does not lift a whole
   * band"*) — and the second kind is what the 10 Sep review is removing from
   * the page. It does not disappear: it moves here, where it is one hover away
   * instead of a paragraph the reader has to step over to reach the data.
   *
   * The split to hold to: **`info` explains a number, `footer` names its
   * source.**
   */
  info?: string
  action?: ReactNode
  children: ReactNode
  footer?: ReactNode
}) {
  return (
    <section
      className="p-5"
      // Shadow means "this floats". A static surface gets a 1px border and
      // nothing else — DESIGN-SYSTEM.md §6.
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
      }}
    >
      {(title || action) && (
        <div className="mb-4 flex items-center gap-3">
          {title && (
            <h2
              className="m-0 text-xs font-semibold uppercase"
              style={{ color: 'var(--text-label)', letterSpacing: '0.06em' }}
            >
              {title}
              {info && <InfoDot note={info} />}
            </h2>
          )}
          {action && <div className="ml-auto">{action}</div>}
        </div>
      )}
      {children}
      {footer && (
        <div className="mt-4 pt-3 text-xs" style={{ borderTop: '1px solid var(--border)', color: 'var(--text-label)' }}>
          {footer}
        </div>
      )}
    </section>
  )
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: ReactNode
}) {
  return (
    <label className="mb-4 block">
      <span className="mb-1 block text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
        {label}
      </span>
      {children}
      {hint && (
        <span className="mt-1 block text-xs" style={{ color: 'var(--text-label)' }}>
          {hint}
        </span>
      )}
    </label>
  )
}

export function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={`w-full px-3 py-2 text-sm ${props.className ?? ''}`}
      style={{
        background: 'var(--bg-page)',
        color: 'var(--text-primary)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-md)',
        ...props.style,
      }}
    />
  )
}

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'quiet' | 'danger'
}

export function Button({ variant = 'secondary', ...props }: ButtonProps) {
  // Mint carries identity, and `--brand-ink` is the mint that can be *read* —
  // but it FLIPS: mint-700 (dark) in light mode, mint-300 (light) in dark mode.
  //
  // So the label cannot be a fixed white. It was, and dark mode measured
  // **1.65:1** — the exact ratio the design system warns about, because it is
  // the logo mint used as if it were a text background. `--bg-surface` flips the
  // opposite way, so the label is simply the page surface inverted against the
  // brand ink:
  //     light   #FFFFFF on mint-700 #0E7A5A  →  5.32:1
  //     dark    #111D2B on mint-300 #94D8B6  → 10.33:1
  //
  // Composed from existing tokens rather than adding a value — DESIGN-SYSTEM §8
  // step 3: "a new component is fine; a new value usually isn't."
  const palette = {
    primary: { background: 'var(--brand-ink)', color: 'var(--bg-surface)', border: 'none' },
    secondary: {
      background: 'var(--bg-surface)',
      color: 'var(--text-primary)',
      border: '1px solid var(--border)',
    },
    quiet: { background: 'transparent', color: 'var(--brand-ink)', border: 'none' },
    // Saturated red belongs to status, and a destructive action is a status.
    danger: {
      background: 'transparent',
      color: 'var(--status-critical)',
      border: '1px solid var(--border)',
    },
  }[variant]

  return (
    <button
      {...props}
      className={`px-3 py-2 text-sm font-semibold ${props.className ?? ''}`}
      style={{
        ...palette,
        borderRadius: 'var(--radius-md)',
        cursor: props.disabled ? 'not-allowed' : 'pointer',
        opacity: props.disabled ? 0.55 : 1,
        ...props.style,
      }}
    />
  )
}

export function Tag({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'brand' | 'warning' }) {
  const palette = {
    neutral: { background: 'var(--bg-page)', color: 'var(--text-secondary)' },
    brand: { background: 'var(--brand-tint)', color: 'var(--brand-ink)' },
    warning: { background: 'var(--status-warning-bg)', color: 'var(--text-primary)' },
  }[tone]
  return (
    <span
      className="inline-block px-2 py-0.5 text-xs font-medium"
      style={{ ...palette, borderRadius: 'var(--radius-sm)', whiteSpace: 'nowrap' }}
    >
      {children}
    </span>
  )
}

/** Errors, and the "nothing here yet" case. Phase 9 checks every page has both. */
export function Note({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'error' | 'good' | 'warning'
  children: ReactNode
}) {
  const palette = {
    info: { background: 'var(--brand-tint)', color: 'var(--text-primary)' },
    error: { background: 'var(--status-critical-bg)', color: 'var(--text-primary)' },
    good: { background: 'var(--status-good-bg)', color: 'var(--text-primary)' },
    // Held on a client answer is neither an error nor neutral information.
    warning: { background: 'var(--status-warning-bg)', color: 'var(--text-primary)' },
  }[tone]
  return (
    <div
      role={tone === 'error' ? 'alert' : undefined}
      className="mb-4 px-3 py-2 text-sm"
      style={{ ...palette, borderRadius: 'var(--radius-md)' }}
    >
      {children}
    </div>
  )
}

/**
 * A loading placeholder.
 *
 * 🔴 **The bars were the page's own background colour** (`--bg-page`). Inside a
 * card that reads, because a card is `--bg-surface`; drawn straight onto the
 * page — Raw Data's first load — it was the same colour as what was behind it,
 * and the page looked blank for seconds (Nilay, 25 Sep). The bars now use the
 * border token, which contrasts with both surfaces in both themes, and pulse
 * unless the reader has asked for reduced motion.
 *
 * `label` adds a visible line of text for a page-level load, where a few grey
 * bars alone do not say that anything is happening.
 */
export function Skeleton({ rows = 3, label }: { rows?: number; label?: string }) {
  return (
    <div aria-busy="true" aria-live="polite" className="skeleton">
      {label && (
        <p className="mb-3 text-sm" style={{ color: 'var(--text-secondary)' }}>
          {label}
        </p>
      )}
      {Array.from({ length: rows }).map((_, index) => (
        <div
          key={index}
          className="skeleton-bar mb-2"
          style={{
            height: '0.75rem',
            width: `${90 - index * 12}%`,
            background: 'var(--border)',
            borderRadius: 'var(--radius-sm)',
          }}
        />
      ))}
      {!label && <span className="sr-only">Loading</span>}
    </div>
  )
}
